import { useEffect, useRef, useState } from 'react';
import { BIOMES, type WorldMapNode } from '@goodtime/shared';
import {
  AudioBus,
  WorldMapScene,
  worldMapOf,
  type RealmMapArt,
  type RealmTileSet,
  type WorldMapHudArt,
} from '@goodtime/engine';
import { LOBBY_MUSIC, SHARED_UI_ICONS } from '../../themes';
import { BUTTON_PLATES } from '../../themes/roadGames';
import { funnelBeacon } from '../../api/funnel';
import { fetchRealmSlots } from '../../api/realm';
import { iconForNode } from '../world/v2/BiomeListScreen';
import { REALM_FLOORS } from './realmFloors';
import { markWorldMapSeen, worldMapSeen, worldSlotInfo } from './worldMapData';
import { safeArea } from './safeArea';
import bezelUrl from '../../assets/road/light_bezel.png';
import arrowUrl from '../../assets/icon/back.png';
import lockUrl from '../../assets/icon/lock.png';
import gemUrl from '../../assets/icon/gem.png';
import friendsUrl from '../../assets/icon/friends.png';
import joinUrl from '../../assets/icon/join.png';
import clockUrl from '../../assets/icon/clock.png';
import giftUrl from '../../assets/icon/gift.png';
import menuUrl from '../../assets/icon/menu.png';
import starUrl from '../../assets/icon/star.png';
import crownUrl from '../../assets/map_node/boss.png';
import mapUrl from '../../assets/icon/map.png';

const ART: RealmMapArt = {
  bezel: bezelUrl,
  arrow: arrowUrl,
  lock: lockUrl,
  gem: gemUrl,
  friends: friendsUrl,
  join: joinUrl,
  schedule: clockUrl,
};

const HUD: WorldMapHudArt = { crown: crownUrl, gift: giftUrl, list: menuUrl, star: starUrl, map: mapUrl };

/** Shared art loaded by alias: the painted button plates the board's
 *  buttons sit on. */
const SHARED = [...Object.values(BUTTON_PLATES), SHARED_UI_ICONS.gem!].flatMap((e) =>
  e.src ? [{ alias: e.alias, src: e.src }] : [],
);

/** How long the map takes to fade in over the screen it replaces. */
export const WORLD_MAP_FADE_MS = 300;

export interface WorldMapProps {
  /** The world (index into BIOMES); it has a map (the engine's worldMapOf). */
  world: number;
  /** The slot to centre on: the one just played, or the building tapped on
   *  the overview; null for the slot the player was last on in this world,
   *  else its first. */
  focusNodeId: number | null;
  unlockedSlotIds: ReadonlyArray<string>;
  /** The slot the player was last on: its plate glows. */
  currentNodeId: number;
  playerId?: string | null;
  /** Whether a road slot's road runs for the player (royal_road). */
  roadOn: (themeId: string) => boolean;
  onPlayNode: (node: WorldMapNode) => void;
  /** Back to the realm map. */
  onBack: () => void;
  onFriends: () => void;
  onJoinWithCode: () => void;
  onSchedule: () => void;
  /** The world's multiplayer Wheel. */
  onMainGame?: (world: number) => void;
  giftReady?: boolean;
  onGift?: () => void;
  /** A dialog covers the map: its life and its opening moments wait. */
  covered?: boolean;
  /** Fade in over the screen it replaces (the realm map) once its opening
   *  view has drawn; without, it shows at once (back from a slot). */
  fadeIn?: boolean;
  /** The map has drawn its opening view (and faded in): the screen it
   *  replaced can go now. */
  onShown?: () => void;
  /** The map has drawn its opening view, at the moment it starts to fade in. */
  onDrawn?: () => void;
  /** The map could not start (a refused WebGL context, no memory): it has
   *  torn itself down and draws nothing; the host leaves the player on the
   *  screen it would have replaced. */
  onFailed?: () => void;
  /** The screen that replaces this one has drawn: fade out over it, and
   *  stop taking taps. */
  leaving?: boolean;
}

/**
 * Hosts one world's own painted map (flag `world_maps`): the Pixi
 * {@link WorldMapScene}, faded in over the realm map once its opening view
 * has drawn. It passes the player's slots through, fetches each slot's
 * stars and road for the plates, remembers which slots the map has shown
 * open on this device (a slot opened since gets its opening moment), and
 * sends the world map's funnel steps.
 *
 * The world's ambient life comes with its pieces: the folder its spec names
 * (`ambient`).
 */
export function WorldMap({
  world,
  focusNodeId,
  unlockedSlotIds,
  currentNodeId,
  playerId,
  roadOn,
  onPlayNode,
  onBack,
  onFriends,
  onJoinWithCode,
  onSchedule,
  onMainGame,
  giftReady = false,
  onGift,
  covered = false,
  fadeIn = true,
  onShown,
  onDrawn,
  onFailed,
  leaving = false,
}: WorldMapProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<WorldMapScene | null>(null);
  const [shown, setShown] = useState(false);
  const [failed, setFailed] = useState(false);
  const handlers = useRef({ onPlayNode, onBack, onFriends, onJoinWithCode, onSchedule, onMainGame, onGift, onShown, onDrawn, onFailed });
  handlers.current = { onPlayNode, onBack, onFriends, onJoinWithCode, onSchedule, onMainGame, onGift, onShown, onDrawn, onFailed };
  const unlockedRef = useRef(unlockedSlotIds);
  unlockedRef.current = unlockedSlotIds;
  const live = useRef({ playerId, roadOn });
  live.current = { playerId, roadOn };
  const biome = BIOMES[world]?.id ?? 'kingdom';

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const scene = new WorldMapScene(host, {
      world,
      art: ART,
      hud: HUD,
      floors: REALM_FLOORS,
      shared: SHARED,
      unlockedSlotIds: unlockedRef.current,
      currentNodeId,
      focusNodeId,
      slotIcon: iconForNode,
      insets: safeArea(),
      seenOpen: worldMapSeen(biome),
      onSeenOpen: (ids) => markWorldMapSeen(biome, ids),
      giftReady,
      ambient: ambientFor(world),
      onPlayNode: (node) => {
        // Which slots players open from a world's map (detail: the node id).
        funnelBeacon('world_map_slot', { playerId: live.current.playerId, detail: String(node.id) });
        handlers.current.onPlayNode(node);
      },
      onBack: () => handlers.current.onBack(),
      onFriends: () => handlers.current.onFriends(),
      onJoinWithCode: () => handlers.current.onJoinWithCode(),
      onSchedule: () => handlers.current.onSchedule(),
      onWheel: () => handlers.current.onMainGame?.(world),
      onGift: () => handlers.current.onGift?.(),
    });
    sceneRef.current = scene;
    const dev = window as unknown as { __worldScene?: WorldMapScene };
    // Covered until it has faded in: an opening moment waits to be seen.
    scene.setAmbientPaused(true);
    let fade: ReturnType<typeof setTimeout> | null = null;
    void scene
      .init()
      .then(() => scene.firstView())
      .then(() => {
        if (sceneRef.current !== scene) return;
        setShown(true);
        handlers.current.onDrawn?.();
        fade = setTimeout(() => handlers.current.onShown?.(), fadeIn ? WORLD_MAP_FADE_MS + 20 : 0);
      })
      .catch((err) => {
        // The map never drew: it sits invisible over the screen it was to
        // replace and would swallow every tap there. Take it down.
        if (sceneRef.current !== scene) return;
        console.warn('[WorldMap] could not start:', err);
        scene.destroy();
        sceneRef.current = null;
        if (dev.__worldScene === scene) delete dev.__worldScene;
        setFailed(true);
        handlers.current.onFailed?.();
      });
    if (import.meta.env.DEV) dev.__worldScene = scene;
    return () => {
      if (fade) clearTimeout(fade);
      scene.destroy();
      sceneRef.current = null;
      if (dev.__worldScene === scene) delete dev.__worldScene;
    };
    // The map opens once per visit; later changes go through the scene.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    sceneRef.current?.setUnlockedSlotIds(unlockedSlotIds);
  }, [unlockedSlotIds]);

  useEffect(() => {
    sceneRef.current?.setAmbientPaused(covered || !shown);
  }, [covered, shown]);

  useEffect(() => {
    sceneRef.current?.setGiftReady(giftReady);
  }, [giftReady]);

  // Each slot's stars and road, for the plates, once the player is known.
  useEffect(() => {
    if (!playerId) return undefined;
    let gone = false;
    fetchRealmSlots(playerId)
      .then((slots) => {
        if (gone) return;
        sceneRef.current?.setSlots(worldSlotInfo(world, slots, (t) => live.current.roadOn(t)));
      })
      .catch((err) => console.warn('[WorldMap] slots not loaded:', err));
    return () => {
      gone = true;
    };
  }, [playerId, world]);

  // Lobby music, as on the realm map; the funnel's world map step.
  useEffect(() => {
    void AudioBus.shared.loadEntry(LOBBY_MUSIC.alias, LOBBY_MUSIC.src);
    AudioBus.shared.playMusic(LOBBY_MUSIC.alias, { fadeInMs: 500 });
    funnelBeacon('world_map_open', { playerId, detail: biome });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failed) return null;
  return (
    <div
      ref={hostRef}
      data-testid="world-map"
      data-world={biome}
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        background: '#4c7a2e',
        touchAction: 'none',
        opacity: leaving ? 0 : shown || !fadeIn ? 1 : 0,
        transition: fadeIn || leaving ? `opacity ${WORLD_MAP_FADE_MS}ms ease-out` : undefined,
        pointerEvents: leaving ? 'none' : undefined,
        // Over the screen it replaces until that goes.
        zIndex: 1,
      }}
    />
  );
}

/** The scene's ambient option for a world with life in this build: the
 *  folder its spec names, and the files in it to start at once. */
function ambientFor(world: number): { base: string; tiles: RealmTileSet; files: readonly string[] } | undefined {
  const spec = worldMapOf(world);
  return spec?.ambient ? { base: spec.ambient, tiles: spec.tiles, files: spec.ambientFiles ?? [] } : undefined;
}
